import React, { useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import "./IDCardGenerator.scss";
import logo from "../../images/hoop.png";

const IDCardGenerator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState("student");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [people, setPeople] = useState([]);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [editingCard, setEditingCard] = useState(null);
  const [editedData, setEditedData] = useState({});
  const cardRef = useRef(null);
  const [bulkErrors, setBulkErrors] = useState([]); // Add this to your state

  // Mock data (same as before)
  const mockStaff = [
    {
      id: "STF001",
      name: "Ram Sharma",
      role: "Science Teacher",
      department: "Science Department",
      joinDate: "2023-01-15",
      phone: "+977-9876543210",
      email: "ram.sharma@hoopschool.edu",
      address: "Kathmandu-15, Nepal", // Add this line
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    },
    // ... add address to all other staff members
  ];

  const mockStudents = {
    Nursery: [
      {
        id: "N001",
        name: "Aarav Thapa",
        class: "Nursery",
        section: "A",
        rollNo: "01",
        dob: "2019-05-15",
        guardian: "Kiran Thapa",
        phone: "+977-9876543212",
        address: "Bhaktapur-12, Nepal", // Add this line
        image:
          "https://images.unsplash.com/photo-1544717297-fa95b6ee9643?w=150&h=150&fit=crop&crop=face",
      },
      // ... add address to all other students
    ],
    // ... other classes
  };

  const classes = Object.keys(mockStudents);

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSelectedClass("");
    setSelectedPerson(null);
    setPeople(type === "staff" ? mockStaff : []);
  };

  const handleClassChange = (className) => {
    setSelectedClass(className);
    setSelectedPerson(null);
    setPeople(className ? mockStudents[className] || [] : []);
  };

  const handlePersonChange = (personId) => {
    const person = people.find((p) => p.id === personId);
    setSelectedPerson(person);
  };

  const handleEditCard = (person) => {
    setEditingCard(person.id);
    setEditedData({ ...person });
  };

  const handleSaveEdit = () => {
    setPeople((prev) =>
      prev.map((person) =>
        person.id === editingCard ? { ...editedData } : person
      )
    );
    if (selectedPerson && selectedPerson.id === editingCard) {
      setSelectedPerson({ ...editedData });
    }
    setEditingCard(null);
    setEditedData({});
  };

  const handleCancelEdit = () => {
    setEditingCard(null);
    setEditedData({});
  };

  const handleDownload = async (person = selectedPerson) => {
    if (!cardRef.current || !person) return;

    // Create a temporary element to render the card for download
    const tempCard = document.createElement("div");
    tempCard.style.position = "absolute";
    tempCard.style.left = "-9999px";
    tempCard.appendChild(cardRef.current.cloneNode(true));
    document.body.appendChild(tempCard);

    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(tempCard.firstChild, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: null,
      });

      // Create download link
      const link = document.createElement("a");
      link.download = `id-card-${person.id}.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating card:", error);
    } finally {
      // Clean up
      document.body.removeChild(tempCard);
    }
  };
  const handleBulkDownload = async () => {
    if (people.length === 0) return;

    setIsBulkGenerating(true);
    setBulkProgress(0);
    setBulkErrors([]); // Reset errors on new download

    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();
    const imgFolder = zip.folder("id-cards");

    for (let i = 0; i < people.length; i++) {
      const person = people[i];
      setSelectedPerson(person);

      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(cardRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
        });

        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png", 1.0)
        );
        imgFolder.file(`${person.id}-${person.name}.png`, blob);

        setBulkProgress(((i + 1) / people.length) * 100);
        await new Promise((resolve) => setTimeout(resolve, 300));
      } catch (error) {
        console.error(`Error generating card for ${person.name}:`, error);
        setBulkErrors((prev) => [...prev, person.name]);
        continue;
      }
    }

    // Generate zip file
    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const link = document.createElement("a");
    link.href = url;
    link.download = "hoop-school-id-cards.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setIsBulkGenerating(false);

    // Show errors if any
    if (bulkErrors.length > 0) {
      alert(`Failed to generate cards for: ${bulkErrors.join(", ")}`);
    }
  };

  const renderIDCard = (person, showEdit = false) => {
    if (!person) return null;

    const isEditing = editingCard === person.id;
    const displayData = isEditing ? editedData : person;

    return (
      <div ref={person === selectedPerson ? cardRef : null} className="id-card">
        {showEdit && (
          <div className="id-card__edit-controls">
            {isEditing ? (
              <div className="edit-buttons">
                <button className="save" onClick={handleSaveEdit}>
                  Save
                </button>
                <button className="cancel" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </div>
            ) : (
              <button className="edit" onClick={() => handleEditCard(person)}>
                Edit
              </button>
            )}
          </div>
        )}

        <div className="id-card__header">
          <div className="logo-container">
            <div className="logo">
              <img src={logo} />
            </div>
            <span className="school-name">HOOP SCHOOL</span>
          </div>
        </div>

        <div className="id-card__body">
          <div className="photo-container">
            <img src={displayData.image} alt={displayData.name} />
          </div>
          <div className="person-info">
            {isEditing ? (
              <input
                type="text"
                value={editedData.name || ""}
                onChange={(e) =>
                  setEditedData({ ...editedData, name: e.target.value })
                }
              />
            ) : (
              <h2>{displayData.name}</h2>
            )}
            <div className="info-sub">
              <p>
                {selectedType === "staff"
                  ? displayData.role
                  : `Class ${displayData.class}`}
              </p>
              <span className="value">{displayData.id}</span>
            </div>
          </div>
          <div className="details">
            <div className="detail-row">
              <span className="label">Address</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedData.address || ""}
                  onChange={(e) =>
                    setEditedData({ ...editedData, address: e.target.value })
                  }
                />
              ) : (
                <span className="value">{displayData.address}</span>
              )}
            </div>

            <div className="detail-row">
              <span className="label">Phone</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedData.phone || ""}
                  onChange={(e) =>
                    setEditedData({ ...editedData, phone: e.target.value })
                  }
                />
              ) : (
                <span className="value">{displayData.phone}</span>
              )}
            </div>

            {selectedType === "staff" ? (
              <div className="detail-row">
                <span className="label">Email</span>
                {isEditing ? (
                  <input
                    type="email"
                    value={editedData.email || ""}
                    onChange={(e) =>
                      setEditedData({ ...editedData, email: e.target.value })
                    }
                  />
                ) : (
                  <span className="value">{displayData.email}</span>
                )}
              </div>
            ) : (
              <>
                <div className="detail-row">
                  <span className="label">DOB</span>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editedData.dob || ""}
                      onChange={(e) =>
                        setEditedData({ ...editedData, dob: e.target.value })
                      }
                    />
                  ) : (
                    <span className="value">{displayData.dob}</span>
                  )}
                </div>
                <div className="detail-row">
                  <span className="label">Roll No</span>
                  <span className="value">{displayData.rollNo}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Guardian</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.guardian || ""}
                      onChange={(e) =>
                        setEditedData({
                          ...editedData,
                          guardian: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <span className="value">{displayData.guardian}</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* <div className="id-card__qrcode">
            <div className="qrcode-container">
              <QRCodeCanvas
                value={JSON.stringify({
                  id: displayData.id,
                  name: displayData.name,
                  type: selectedType,
                })}
                size={60}
              />
            </div>
          </div> */}
          {/* Add this new footer section */}
          <div className="id-card__footer">
            <div className="powered-by">
              Powered by <strong>HOOP</strong>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="id-card-generator__step-content step-1">
            <h2>Step 1: Select Type and People</h2>

            <div className="type-selector">
              <label>Select Type:</label>
              <div className="buttons">
                <button
                  className={selectedType === "student" ? "active" : ""}
                  onClick={() => handleTypeChange("student")}
                >
                  Student
                </button>
                <button
                  className={selectedType === "staff" ? "active" : ""}
                  onClick={() => handleTypeChange("staff")}
                >
                  Staff
                </button>
              </div>
            </div>

            {selectedType === "student" && (
              <div className="class-selector">
                <label>Select Class:</label>
                <select
                  value={selectedClass}
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  <option value="">Choose a class</option>
                  {classes.map((cls) => (
                    <option key={cls} value={cls}>
                      {cls}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {people.length > 0 && (
              <div>
                <p>
                  Found {people.length}{" "}
                  {selectedType === "staff" ? "staff members" : "students"}
                </p>
                <button
                  className="preview-button"
                  onClick={() => setCurrentStep(2)}
                >
                  Preview Cards
                </button>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="id-card-generator__step-content step-2">
            <div className="header">
              <h2>Step 2: Preview & Edit Cards</h2>
              <div className="buttons">
                <button className="back" onClick={() => setCurrentStep(1)}>
                  Back
                </button>
                <button className="next" onClick={() => setCurrentStep(3)}>
                  Next: Download
                </button>
              </div>
            </div>

            <div className="cards-grid">
              {people.map((person) => (
                <div key={person.id}>{renderIDCard(person, true)}</div>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="id-card-generator__step-content step-3">
            <div className="header">
              <h2>Step 3: Download Cards</h2>
              <button className="back-button" onClick={() => setCurrentStep(2)}>
                Back to Preview
              </button>
            </div>

            <div className="download-container">
              <div className="download-options">
                <h3>Download Options</h3>

                <div className="select-person">
                  <label>Select Individual Card:</label>
                  <select
                    value={selectedPerson?.id || ""}
                    onChange={(e) => handlePersonChange(e.target.value)}
                  >
                    <option value="">Choose a person</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>

                  {selectedPerson && (
                    <button
                      className="download-button"
                      onClick={() => handleDownload(selectedPerson)}
                    >
                      Download Selected Card
                    </button>
                  )}
                </div>

                <div className="bulk-download">
                  <button
                    className="bulk-button"
                    onClick={handleBulkDownload}
                    disabled={isBulkGenerating}
                  >
                    {isBulkGenerating
                      ? `Generating... ${Math.round(bulkProgress)}%`
                      : `Download All Cards (${people.length})`}
                  </button>

                  {isBulkGenerating && (
                    <div className="progress-bar">
                      <div
                        className="progress"
                        style={{ width: `${bulkProgress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card-preview">
                {selectedPerson ? (
                  renderIDCard(selectedPerson)
                ) : (
                  <div className="empty-preview">
                    <div className="icon">🆔</div>
                    <p>Select a person to preview their ID card</p>
                    <small>
                      {people.length}{" "}
                      {selectedType === "staff" ? "staff members" : "students"}{" "}
                      available
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="id-card-generator box fix-height">
      <div className="id-card-generator__header">
        <h1>ID Card Generator</h1>
      </div>

      <div className="id-card-generator__step-indicator">
        <div className="step-container">
          {[1, 2, 3].map((step) => (
            <div key={step} className="step">
              <div
                className={`step-number ${currentStep >= step ? "active" : ""}`}
              >
                {step}
              </div>
              {step < 3 && (
                <div
                  className={`step-connector ${
                    currentStep > step ? "active" : ""
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {renderStepContent()}
      {isBulkGenerating && (
        <div className="bulk-loading-overlay">
          <p>Generating {Math.round(bulkProgress)}% complete...</p>
          <p>Please don't close this window.</p>
        </div>
      )}
    </div>
  );
};

export default IDCardGenerator;
